"use client"

export const dynamic = 'force-dynamic'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { CheckCircle, AlertCircle, Clock, Globe, Zap, Shield } from "lucide-react"
import { Navigation } from "@/components/navigation"
import { Footer } from "@/components/footer"

export default function StatusPage() {
  // Mock data for status indicators
  const services = [
    { name: "API Service", status: "operational", uptime: "99.98%" },
    { name: "Translation Engine", status: "operational", uptime: "99.95%" },
    { name: "File Upload", status: "operational", uptime: "99.99%" },
    { name: "Authentication", status: "operational", uptime: "99.97%" },
    { name: "Download Service", status: "operational", uptime: "99.96%" },
    { name: "Dashboard", status: "operational", uptime: "99.99%" }
  ]

  const incidents = [
    {
      date: "Dec 15, 2025",
      title: "Temporary API slowdown",
      status: "resolved",
      description: "Some users experienced slower response times due to increased load. Resolved by scaling infrastructure.",
      duration: "23 minutes"
    },
    {
      date: "Dec 10, 2025", 
      title: "Scheduled maintenance",
      status: "completed",
      description: "Routine database maintenance and security updates.",
      duration: "45 minutes"
    },
    {
      date: "Dec 5, 2025",
      title: "OpenAI API rate limit issues",
      status: "resolved",
      description: "Temporary translation delays due to OpenAI API rate limiting. Implemented better retry logic.",
      duration: "1 hour 12 minutes"
    }
  ]

  const metrics = [
    { label: "Overall Uptime", value: "99.97%", period: "Last 30 days" },
    { label: "Average Response Time", value: "145ms", period: "Last 24 hours" },
    { label: "Successful Translations", value: "99.8%", period: "Last 7 days" },
    { label: "API Success Rate", value: "99.9%", period: "Last 24 hours" }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      <Navigation />

      <section className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
              Service Status
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Real-time status and performance metrics for all Locsy services
            </p>
          </div>

          {/* Overall Status */}
          <Card className="mb-8 border-l-4 border-l-green-500">
            <CardHeader>
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <div>
                  <CardTitle className="text-green-600">All Systems Operational</CardTitle>
                  <CardDescription>All services are running normally</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {metrics.map((metric, index) => (
              <Card key={index}>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600 mb-1">{metric.value}</div>
                  <div className="text-sm font-medium mb-1">{metric.label}</div>
                  <div className="text-xs text-muted-foreground">{metric.period}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Service Status */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">Service Status</h2>
            <div className="space-y-4">
              {services.map((service, index) => (
                <Card key={index}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <div>
                          <div className="font-medium">{service.name}</div>
                          <div className="text-sm text-muted-foreground">Operational</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium">{service.uptime}</div>
                        <div className="text-xs text-muted-foreground">30-day uptime</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Performance Chart Placeholder */}
          <Card className="mb-12">
            <CardHeader>
              <CardTitle>Response Time (Last 24 Hours)</CardTitle>
              <CardDescription>Average API response times in milliseconds</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 bg-muted/30 rounded-lg flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <Zap className="h-8 w-8 mx-auto mb-2" />
                  <p>Performance chart would be displayed here</p>
                  <p className="text-sm">Average: 145ms | Peak: 280ms | Low: 98ms</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Incidents */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">Recent Incidents</h2>
            <div className="space-y-4">
              {incidents.map((incident, index) => (
                <Card key={index}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          {incident.status === "resolved" ? (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          ) : (
                            <AlertCircle className="h-5 w-5 text-amber-600" />
                          )}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{incident.title}</CardTitle>
                          <CardDescription className="mt-1">{incident.description}</CardDescription>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant={incident.status === "resolved" ? "secondary" : "destructive"}>
                          {incident.status}
                        </Badge>
                        <div className="text-sm text-muted-foreground mt-1">{incident.date}</div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      Duration: {incident.duration}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* System Information */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card>
              <CardHeader className="text-center">
                <Globe className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                <CardTitle>Global Availability</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-sm text-muted-foreground">
                  Served from multiple regions worldwide for optimal performance
                </p>
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>US East</span>
                    <Badge variant="secondary">Operational</Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>EU West</span>
                    <Badge variant="secondary">Operational</Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Asia Pacific</span>
                    <Badge variant="secondary">Operational</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="text-center">
                <Shield className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <CardTitle>Security Status</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  All security systems functioning normally
                </p>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>SSL/TLS</span>
                    <Badge variant="secondary">Active</Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>DDoS Protection</span>
                    <Badge variant="secondary">Active</Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Monitoring</span>
                    <Badge variant="secondary">Active</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="text-center">
                <Zap className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                <CardTitle>API Performance</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  Current API performance metrics
                </p>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Requests/sec</span>
                    <span className="font-medium">1,247</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Error Rate</span>
                    <span className="font-medium">0.1%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>P95 Latency</span>
                    <span className="font-medium">285ms</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Subscribe to Updates */}
          <Card>
            <CardHeader className="text-center">
              <CardTitle>Stay Updated</CardTitle>
              <CardDescription>
                Subscribe to status updates and be notified of any service interruptions
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-sm text-muted-foreground mb-4">
                You can also follow us on social media or join our Discord server for real-time updates.
              </p>
              <div className="flex justify-center gap-4">
                <Badge variant="outline">Twitter: @ai_i18n</Badge>
                <Badge variant="outline">Discord: locsy.ai/discord</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  )
} 